import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useCurrency } from '../context/CurrencyContext';
import { useCart } from '../context/CartContext';
import PublicProductsSection from '../components/ui/PublicProductsSection';
import HomeLandingHero from '../components/ui/HomeLandingHero';
import StorefrontLayout from '../components/layout/StorefrontLayout';
import { getRecommendationSessionId, trackRecommendationEvent } from '../utils/recommendations';
import TestimonialsCarousel from '../components/ui/TestimonialsCarousel';
import ConfigurableLandingSections from '../components/storefront/ConfigurableLandingSections';
import ShopCategoriesStrip from '../components/storefront/ShopCategoriesStrip';
import { DEFAULT_LANDING_SECTIONS, type LandingSection } from '../components/storefront/landingSectionsCms';
import { applyContentLocaleToUrl } from '../utils/contentLocaleQuery';
import {
  DEFAULT_DEPARTMENT_SLUG,
  departmentPath,
  useDepartmentSlug,
} from '../utils/departmentRouting';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type PublicCategory = { id: string; name: string; slug: string };
type PublicProduct = {
  id: string;
  name: string;
  imageUrl?: string | null;
  pricePerUnit: number;
  salePrice?: number | null;
  baseUnit: 'LB' | 'KG';
  currentStockLbs?: number;
  isPreorderEnabled?: boolean;
  preorderEta?: string | null;
  category?: PublicCategory | null;
};
type SiteContentMeta = {
  storefrontStatCustomers?: string;
  storefrontStatSales?: string;
  storefrontStatTowns?: string;
  landingSections?: LandingSection[];
};

type HomeRecommendations = {
  disabled: boolean;
  sections: {
    trending: PublicProduct[];
    bestSellers: PublicProduct[];
    personalized: PublicProduct[];
    categoryShelves: Array<{ category: PublicCategory; products: PublicProduct[] }>;
  };
};

function parseCounter(raw: string | undefined) {
  const input = String(raw || '').trim();
  const numeric = input.replace(/[^\d.]/g, '');
  const end = Number(numeric || 0);
  const startIdx = input.indexOf(numeric);
  const prefix = startIdx > 0 ? input.slice(0, startIdx) : '';
  const suffix = startIdx >= 0 ? input.slice(startIdx + numeric.length) : '';
  return { end, prefix, suffix };
}

function StatCard({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string | undefined;
  tone?: 'emerald' | 'blue' | 'amber' | 'neutral';
}) {
  const parsed = parseCounter(value);
  const toneClass =
    tone === 'neutral'
      ? {
          card: 'border-slate-200/85 bg-white/90',
          label: 'text-slate-500',
          value: 'text-slate-900',
        }
      : tone === 'blue'
        ? {
            card: 'border-sky-200/80 bg-sky-50/40',
            label: 'text-sky-700',
            value: 'text-sky-950',
          }
        : tone === 'amber'
          ? {
              card: 'border-amber-200/80 bg-amber-50/35',
              label: 'text-amber-700',
              value: 'text-amber-950',
            }
          : {
              card: 'border-emerald-200/80 bg-emerald-50/35',
              label: 'text-emerald-700',
              value: 'text-emerald-950',
            };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={`group rounded-2xl border px-6 py-7 text-center shadow-[0_8px_24px_rgba(15,23,42,0.05)] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(15,23,42,0.08)] sm:px-8 sm:py-8 ${toneClass.card}`}
    >
      <p className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${toneClass.label}`}>{label}</p>
      <p className={`mt-3 text-3xl font-extrabold tabular-nums tracking-tight sm:text-4xl ${toneClass.value}`}>
        {parsed.end > 0 ? (
          <CountUp
            start={0}
            end={parsed.end}
            duration={2.1}
            separator=","
            prefix={parsed.prefix}
            suffix={parsed.suffix}
            enableScrollSpy
            scrollSpyOnce
            preserveValue
          />
        ) : (
          value || '0'
        )}
      </p>
    </motion.div>
  );
}

function ProductRail({
  title,
  subtitle,
  products,
  source,
  addToCartLabel,
  departmentSlug,
}: {
  title: string;
  subtitle?: string;
  products: PublicProduct[];
  source: string;
  addToCartLabel: string;
  departmentSlug: string;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const { addLine } = useCart();
  if (!products.length) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-xl border border-slate-200/80 bg-white p-5 sm:p-7"
    >
      <div className="mb-5 border-b border-slate-100 pb-4">
        <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">{title}</h2>
        {subtitle ? <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{subtitle}</p> : null}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {products.map((p) => {
          const imageSrc =
            p.imageUrl && p.imageUrl.startsWith('http')
              ? p.imageUrl
              : p.imageUrl
                ? `${API_BASE}${p.imageUrl}`
                : undefined;
          const unitPrice = Number(p.salePrice ?? p.pricePerUnit);
          const stockQty =
            typeof p.currentStockLbs === 'number' ? Math.max(0, p.currentStockLbs) : null;
          const canAddToCart = stockQty === null || stockQty > 0;
          const canPreorder = !canAddToCart && Boolean(p.isPreorderEnabled);
          const nextArrivalDateLabel =
            p.preorderEta != null && String(p.preorderEta).trim().length > 0
              ? String(p.preorderEta).slice(0, 10)
              : null;
          return (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.35 }}
              className={`group overflow-hidden rounded-lg border border-slate-200 bg-white transition-colors hover:border-slate-300 ${
                !canAddToCart ? 'opacity-95' : ''
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  void trackRecommendationEvent({
                    productId: p.id,
                    eventType: 'RECOMMENDATION_CLICK',
                    context: source,
                    source: 'home',
                  });
                  navigate(departmentPath(departmentSlug, `/products/${p.id}`));
                }}
                className="w-full text-left"
              >
                <div className="relative flex h-36 items-center justify-center bg-slate-50 p-4">
                  {imageSrc ? (
                    <img src={imageSrc} alt={p.name} className="max-h-full max-w-full object-contain" />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-slate-200" />
                  )}
                  {stockQty !== null && (
                    <span
                      className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white ${
                        canAddToCart ? 'bg-slate-900/80' : 'bg-amber-700/95'
                      }`}
                    >
                      {canAddToCart
                        ? p.baseUnit === 'KG'
                          ? t('storefront.cart_stock_line_kg', {
                              qty: stockQty.toFixed(1),
                            })
                          : t('storefront.products_in_stock_lb', {
                              qty: stockQty.toFixed(1),
                            })
                        : t('storefront.out_of_stock')}
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <p className="line-clamp-1 text-sm font-semibold text-slate-900">{p.name}</p>
                  <p
                    className="mt-1 text-sm font-bold"
                    style={{ color: 'var(--storefront-brand-primary, #059669)' }}
                  >
                    {formatPrice(unitPrice)}
                    <span className="ml-1 text-xs font-normal text-slate-500">
                      / {p.baseUnit.toLowerCase()}
                    </span>
                  </p>
                  {canPreorder ? (
                    <p className="mt-1 text-[11px] text-indigo-700">
                      {nextArrivalDateLabel
                        ? t('storefront.preorder_next_arrival', {
                            defaultValue: 'Estimated Next arrival: {{date}}',
                            date: nextArrivalDateLabel,
                          })
                        : t('storefront.preorder_next_arrival_tbd', {
                            defaultValue: 'Estimated Next arrival: TBA',
                          })}
                    </p>
                  ) : null}
                </div>
              </button>
              <div className="px-3 pb-3">
                <button
                  type="button"
                  disabled={!canAddToCart && !canPreorder}
                  onClick={() => {
                    if (!canAddToCart && !canPreorder) return;
                    addLine({ productId: p.id, qty: 1, unit: p.baseUnit });
                    void trackRecommendationEvent({
                      productId: p.id,
                      eventType: 'ADD_TO_CART',
                      context: source,
                      source: 'home',
                    });
                  }}
                  aria-label={
                    canAddToCart
                      ? addToCartLabel
                      : canPreorder
                        ? t('storefront.preorder_now', {
                            defaultValue: 'Pre-order now',
                          })
                        : t('storefront.out_of_stock')
                  }
                  className={`w-full rounded-xl border px-3 py-2.5 text-xs font-semibold transition-colors ${
                    canAddToCart || canPreorder
                      ? 'border-slate-200 bg-slate-50/80 text-slate-800 hover:border-[color:var(--storefront-brand-primary)] hover:bg-emerald-50/80 hover:text-[color:var(--storefront-brand-primary)]'
                      : 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500'
                  }`}
                >
                  {canAddToCart
                    ? addToCartLabel
                    : canPreorder
                      ? t('storefront.preorder_now', {
                          defaultValue: 'Pre-order now',
                        })
                      : t('storefront.out_of_stock')}
                </button>
              </div>
            </motion.article>
          );
        })}
      </div>
    </motion.section>
  );
}

function HomePage() {
  const { t, i18n } = useTranslation();
  const params = useParams<{ departmentSlug?: string }>();
  const hasDepartmentContext = Boolean(params.departmentSlug);
  const departmentSlug = useDepartmentSlug() ?? DEFAULT_DEPARTMENT_SLUG;
  const [allDepartments, setAllDepartments] = React.useState<Array<{
    id: string;
    name: string;
    slug: string;
    description?: string | null;
  }>>([]);
  const [recs, setRecs] = React.useState<HomeRecommendations | null>(null);
  const [siteMeta, setSiteMeta] = React.useState<SiteContentMeta>({
    storefrontStatCustomers: '120000+',
    storefrontStatSales: '280000+',
    storefrontStatTowns: '130+',
  });
  const [landingSections, setLandingSections] = React.useState<LandingSection[]>([]);

  React.useEffect(() => {
    if (!hasDepartmentContext) return;
    let cancelled = false;
    void (async () => {
      try {
        const sessionId = getRecommendationSessionId();
        const url = new URL(
          applyContentLocaleToUrl(
            `${API_BASE}/public/recommendations/home`,
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        url.searchParams.set('departmentSlug', departmentSlug);
        url.searchParams.set('sessionId', sessionId);
        const res = await fetch(url.toString());
        if (!res.ok) return;
        const data = (await res.json()) as HomeRecommendations;
        if (!cancelled) setRecs(data);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [departmentSlug, hasDepartmentContext, i18n.language, i18n.resolvedLanguage]);

  React.useEffect(() => {
    if (hasDepartmentContext) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/departments/public`);
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as Array<{
          id: string;
          name: string;
          slug: string;
          description?: string | null;
        }>;
        if (!cancelled) {
          setAllDepartments(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) setAllDepartments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasDepartmentContext]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/public/site-content`,
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok) {
          if (!cancelled) setLandingSections(DEFAULT_LANDING_SECTIONS);
          return;
        }
        const data = (await res.json()) as SiteContentMeta;
        if (!cancelled) {
          setSiteMeta((prev) => ({
            storefrontStatCustomers:
              data.storefrontStatCustomers?.trim() || prev.storefrontStatCustomers,
            storefrontStatSales:
              data.storefrontStatSales?.trim() || prev.storefrontStatSales,
            storefrontStatTowns:
              data.storefrontStatTowns?.trim() || prev.storefrontStatTowns,
          }));
          if (Array.isArray(data.landingSections) && data.landingSections.length) {
            setLandingSections(data.landingSections);
          } else {
            setLandingSections(DEFAULT_LANDING_SECTIONS);
          }
        }
      } catch {
        if (!cancelled) setLandingSections(DEFAULT_LANDING_SECTIONS);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language, i18n.resolvedLanguage]);

  return (
    <StorefrontLayout contentContained={false} mainFlushTop mainClassName="bg-transparent">
      <HomeLandingHero />

      <section
        className="relative overflow-hidden py-14 sm:py-20"
        style={{ backgroundColor: 'var(--storefront-body-bg, #f1f5f9)' }}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-300/45 to-transparent"
          aria-hidden
        />
        <div className="mx-auto flex max-w-7xl flex-col gap-16 px-4 sm:gap-20 sm:px-6 lg:px-8">
          <section className="border-t border-slate-200/80 pt-12 sm:pt-14">
            <p className="text-center text-[11px] font-bold uppercase tracking-[0.26em] text-slate-600">
              {t('storefront.home_stats_eyebrow')}
            </p>
            <div className="mx-auto mt-8 max-w-5xl rounded-3xl border border-slate-200/70 bg-white p-4 shadow-[0_16px_40px_rgba(15,23,42,0.05)] sm:max-w-none sm:p-6">
              <div className="grid gap-3 sm:grid-cols-3 sm:gap-4">
                <StatCard
                  label={t('storefront.home_stat_customers')}
                  value={siteMeta.storefrontStatCustomers}
                  tone="neutral"
                />
                <StatCard
                  label={t('storefront.home_stat_sales')}
                  value={siteMeta.storefrontStatSales}
                  tone="neutral"
                />
                <StatCard
                  label={t('storefront.home_stat_towns')}
                  value={siteMeta.storefrontStatTowns}
                  tone="neutral"
                />
              </div>
            </div>
          </section>
        </div>

        {hasDepartmentContext ? <ShopCategoriesStrip /> : null}

        <div className="mx-auto flex max-w-7xl flex-col gap-16 px-4 sm:gap-20 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-10">
            {hasDepartmentContext ? (
              <>
                <ProductRail
                  title={t('storefront.home_rail_trending_title')}
                  subtitle={t('storefront.home_rail_trending_sub')}
                  products={recs?.sections?.trending || []}
                  source="home_trending"
                  addToCartLabel={t('storefront.add_to_cart')}
                  departmentSlug={departmentSlug}
                />
                <ProductRail
                  title={t('storefront.home_rail_bestsellers_title')}
                  subtitle={t('storefront.home_rail_bestsellers_sub')}
                  products={recs?.sections?.bestSellers || []}
                  source="home_best_sellers"
                  addToCartLabel={t('storefront.add_to_cart')}
                  departmentSlug={departmentSlug}
                />
                <ProductRail
                  title={t('storefront.home_rail_personal_title')}
                  subtitle={t('storefront.home_rail_personal_sub')}
                  products={recs?.sections?.personalized || []}
                  source="home_personalized"
                  addToCartLabel={t('storefront.add_to_cart')}
                  departmentSlug={departmentSlug}
                />
                {(recs?.sections?.categoryShelves || []).map((shelf) => (
                  <ProductRail
                    key={shelf.category.id}
                    title={shelf.category.name}
                    subtitle={t('storefront.home_rail_category_sub')}
                    products={shelf.products}
                    source={`home_category_${shelf.category.slug}`}
                    addToCartLabel={t('storefront.add_to_cart')}
                    departmentSlug={departmentSlug}
                  />
                ))}
              </>
            ) : (
              allDepartments.map((dep) => (
                <section key={dep.id} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6">
                  <PublicProductsSection
                    mode="home"
                    departmentSlug={dep.slug}
                    departmentName={dep.name}
                    departmentDescription={dep.description ?? undefined}
                    departmentDetailPath={departmentPath(dep.slug)}
                  />
                </section>
              ))
            )}
          </div>

          <ConfigurableLandingSections
            sections={landingSections}
            departmentSlug={hasDepartmentContext ? departmentSlug : null}
          />
        </div>
      </section>

      {hasDepartmentContext ? (
        <div id="products">
          <PublicProductsSection mode="home" departmentSlug={departmentSlug} />
        </div>
      ) : null}

      <section
        id="features"
        className="border-t border-slate-200/40 py-16 sm:py-20"
        style={{ backgroundColor: 'var(--storefront-body-bg, #f1f5f9)' }}
      >
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-12 text-center">
            <p
              className="text-xs font-bold uppercase tracking-[0.2em]"
              style={{ color: 'var(--storefront-brand-primary, #059669)' }}
            >
              {t('storefront.home_features_eyebrow')}
            </p>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              {t('storefront.home_features_title')}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-slate-600">
              {t('storefront.home_features_subtitle')}
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="flex h-full flex-col rounded-3xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/90 p-8 shadow-md shadow-slate-900/[0.04]">
              <div
                className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl text-xl"
                style={{
                  color: 'var(--storefront-brand-primary, #059669)',
                  backgroundColor: 'color-mix(in srgb, var(--storefront-brand-primary, #059669) 14%, white)',
                }}
              >
                <i className="fa fa-user-shield" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">
                {t('storefront.home_feature_iam_title')}
              </h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                {t('storefront.home_feature_iam_body')}
              </p>
            </div>
            <div className="flex h-full flex-col rounded-3xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/90 p-8 shadow-md shadow-slate-900/[0.04]">
              <div
                className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl text-xl"
                style={{
                  color: 'var(--storefront-nav-accent, #ea580c)',
                  backgroundColor: 'color-mix(in srgb, var(--storefront-nav-accent, #ea580c) 14%, white)',
                }}
              >
                <i className="fa fa-warehouse" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">
                {t('storefront.home_feature_inventory_title')}
              </h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                {t('storefront.home_feature_inventory_body')}
              </p>
            </div>
            <div className="flex h-full flex-col rounded-3xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/90 p-8 shadow-md shadow-slate-900/[0.04]">
              <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-xl text-slate-700">
                <i className="fa fa-chart-line" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">
                {t('storefront.home_feature_reporting_title')}
              </h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                {t('storefront.home_feature_reporting_body')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <TestimonialsCarousel />

    </StorefrontLayout>
  );
}

export default HomePage;
